import * as action from 'actions';
import { call, put, takeEvery } from 'redux-saga/effects';
import * as apiBusiness from 'api/business/business';
import * as apiKYB from 'api/kyb';
import * as apiBankAcc from 'api/bankAccounts';
import * as actKyb from 'actions/kyb';
import * as apiOutlet from 'api/outlets';
import * as actionNotification from 'actions/notifications';
import { initialize } from 'redux-form';
import FormData from 'form-data';
import {
  SUBMIT_ENTITY_FORM,
  SUBMIT_BUSINESS_INFO_FORM,
  SUBMIT_BANK_INFO_FORM,
  SUBMIT_BUSINESS_DOCUMENT_FORM,
  UPLOAD_FILE,
  BUSINESS_DATA,
  CONFIRM_REVIEW,
} from 'actions/kyb/constants';

export function* submitEntityFormSaga({ payload }) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  const { data, editMode } = payload;
  try {
    const response = yield call(apiBusiness.updateBusiness, data);
    yield put(actKyb.kybData(response.data.data));
    yield put(actKyb.goToStep(editMode ? 5 : 2));
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* submitBusinessInfoFormSaga({ payload }) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  const { values, editMode } = payload;
  try {
    const response = yield call(apiBusiness.updateBusiness, values);
    yield put(actKyb.kybData(response.data.data));
    const { data: outletsResponse } = yield call(apiOutlet.outlets);
    if (outletsResponse.length === 1) {
      const outlet = outletsResponse[0];
      const outletData = {
        name: outlet.name,
        address: values.address,
        phone_number: outlet.phone,
        kecamatan: values.kecamatan,
        city: values.city,
        province: values.province,
        postal_code: values.postal_code,
        business_id: outlet.business_id,
      };
      yield call(apiOutlet.putOutletById, outlet.id, outletData);
    }
    yield put(actKyb.goToStep(editMode ? 5 : 3));
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* submitBankInfoFormSaga({ payload }) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  const { values, businessId, kybBankData, multipleBankAccountV2 } = payload;
  const dataToSend = {
    account_holder_name: values.name,
    bank_id: parseInt(values.bankName),
    account_holder_number: values.accountNumber,
    business_id: businessId,
  };
  try {
    if (multipleBankAccountV2) {
      const updatedKybBankData = dataToSend;
      dataToSend.is_default = true;

      if (kybBankData === null) {
        const response = yield call(
          apiBankAcc.postNewBankAccountV2,
          dataToSend
        );
        updatedKybBankData.business_bank_account_id =
          response.data.data.business_bank_account_id;
      } else {
        const bankAccountId = kybBankData.business_bank_account_id;
        if (
          values.name !== kybBankData.account_holder_name ||
          values.accountNumber !== kybBankData.account_holder_number ||
          values.bankName !== kybBankData.bank_id
        ) {
          yield call(apiBankAcc.updateBankAccountV2, dataToSend, bankAccountId);
        }
        updatedKybBankData.business_bank_account_id = bankAccountId;
      }
      yield put(actKyb.kybBankData(updatedKybBankData));
    } else {
      const response = yield call(apiKYB.addBankInfo, dataToSend, businessId);
      yield put(actKyb.kybBankData(response.data));
    }

    yield put(actKyb.goToStep(5));
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* businessDataSaga({ payload }) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  const { businessId } = payload;
  try {
    const response = yield call(apiKYB.getBusinessData, businessId);
    const { data } = response;

    yield put(
      initialize('BusinessInfoForm', {
        name: data.name,
        address: data.address,
        province: data.province,
        city: data.city,
        kecamatan: data.kecamatan,
        zipcode: data.postal_code,
      })
    );

    yield put(
      initialize('BusinessEntityForm', {
        businessEntity: data.businessEntityType,
      })
    );

    yield put(
      action.getDropdownData({
        province: data.province,
        city: data.city,
        kecamatan: data.kecamatan,
      })
    );

    const { data: bankDataResponse } = yield call(
      apiKYB.getBankData,
      businessId
    );
    const {
      data: { data_requirement_code: identityResponse },
    } = yield call(apiKYB.getIdentity, businessId);
    if (identityResponse.IMAGE_URL) {
      yield put(actKyb.IdCardData(identityResponse.IMAGE_URL));
      yield put(actKyb.kybIdentityData(identityResponse));
    }
    if (identityResponse.NPWP_IMAGE_URL) {
      yield put(actKyb.npwpImageData(identityResponse.NPWP_IMAGE_URL));
      yield put(actKyb.kybIdentityData(identityResponse));
    }
    let bankData = null;
    bankDataResponse.map(function(bankAccount) {
      if (bankAccount.is_default === true) {
        bankData = bankAccount;
      }
    });
    yield put(actKyb.kybBankData(bankData));
    yield put(
      actKyb.kybData({
        ...data,
        business_entity_type: data.businessEntityType,
      })
    );
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* submitBusinessDocumentFormSaga({ payload }) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  const identity = {
    business_id: payload.businessId,
    data_requirement_code: {
      KTP_NUMBER: payload.KTP_NUMBER,
      KTP_NAME: payload.KTP_NAME,
      IMAGE_URL: payload.IMAGE_URL,
    },
  };
  const { editMode } = payload;
  try {
    const {
      data: { data_requirement_code: identityResponse },
    } = yield call(apiKYB.updateIdentityInfo, identity, payload.businessId);
    yield put(actKyb.kybIdentityData(identityResponse));
    yield put(actKyb.goToStep(editMode ? 5 : 4));
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* uploadFileSaga({ payload }) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  const { file, id } = payload.data;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('id', id);
  formData.append('namespace', 'business-identities');
  try {
    const { data: uploadResponse } = yield call(
      apiKYB.uploadFile,
      formData,
      id
    );
    yield put(actKyb.IdCardData(uploadResponse.download_image_url));
    $('#upload-photo').modal('hide');
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* confirmReviewSaga() {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  try {
    yield call(apiKYB.confirmReview);
    yield put(actKyb.goToStep(6));
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export default function* kybSaga() {
  yield takeEvery(SUBMIT_ENTITY_FORM, submitEntityFormSaga);
  yield takeEvery(SUBMIT_BUSINESS_INFO_FORM, submitBusinessInfoFormSaga);
  yield takeEvery(SUBMIT_BANK_INFO_FORM, submitBankInfoFormSaga);
  yield takeEvery(
    SUBMIT_BUSINESS_DOCUMENT_FORM,
    submitBusinessDocumentFormSaga
  );
  yield takeEvery(BUSINESS_DATA, businessDataSaga);
  yield takeEvery(UPLOAD_FILE, uploadFileSaga);
  yield takeEvery(CONFIRM_REVIEW, confirmReviewSaga);
}
